/**
 * The asset's success cases as cards. Readers without `asset.view.internal` get only confirmed-shareable cases,
 * already anonymized by the server; managers add, edit, share, make internal, link and unlink.
 */

import { useState } from 'react';
import { Link2, Plus, Trophy } from 'lucide-react';

import { useCases } from '@/api/queries/cases';
import type { AssetOut } from '@/api/types';
import type { CaseOut } from '@/api/types/cases';
import { useSession } from '@/session/SessionContext';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';

import { useRevision } from '../materials/useRevision';
import { CaseCard, type CaseAction } from './CaseCard';
import { LinkCaseDialog, MakeInternalDialog, UnlinkCaseDialog } from './CaseDialogs';
import { CaseEditor } from './CaseEditor';
import { ShareCaseDialog } from './ShareCaseDialog';
import '../materials/catalog-tabs.css';

type Open = { kind: 'add' } | { kind: 'link' } | { kind: CaseAction; item: CaseOut };

function Toolbar({ onOpen }: { onOpen: (open: Open) => void }) {
  const { can } = useSession();
  if (!can('asset.manage')) return null;
  return (
    <div className="catalog-tab-toolbar">
      {can('asset.view.internal') && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpen({ kind: 'add' })}>
          <Plus aria-hidden="true" /> Add case
        </button>
      )}
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpen({ kind: 'link' })}>
        <Link2 aria-hidden="true" /> Link existing case
      </button>
    </div>
  );
}

function Dialogs({ open, asset, cases, setOpen }: { open: Open | null; asset: AssetOut; cases: CaseOut[]; setOpen: (o: Open | null) => void }) {
  const { revision, saw } = useRevision(asset.revision);
  const writer = { assetId: asset.id, revision, saw };
  const close = () => setOpen(null);
  if (!open) return null;
  switch (open.kind) {
    case 'add':
      return <CaseEditor asset={asset} onClose={close} onShare={(item) => setOpen({ kind: 'share', item })} />;
    case 'link':
      return <LinkCaseDialog writer={writer} linked={cases.map((c) => c.id)} onClose={close} />;
    case 'edit':
      return <CaseEditor asset={asset} item={open.item} onClose={close} onShare={(item) => setOpen({ kind: 'share', item })} />;
    case 'share':
      return <ShareCaseDialog item={open.item} onClose={close} />;
    case 'internal':
      return <MakeInternalDialog item={open.item} onClose={close} />;
    default:
      return <UnlinkCaseDialog writer={writer} item={open.item} onClose={close} />;
  }
}

function CaseList({ cases, onOpen }: { cases: CaseOut[]; onOpen: (open: Open) => void }) {
  const { can } = useSession();
  if (cases.length === 0) {
    return (
      <StateCard icon={<Trophy aria-hidden="true" />} title="No success cases yet">
        <p>{can('asset.manage') ? 'Add a case or link one that already backs another asset.' : 'Cases where this asset made a difference appear here.'}</p>
      </StateCard>
    );
  }
  return (
    <div className="case-grid">
      {cases.map((c) => (
        <CaseCard key={c.id} item={c} onAction={(kind) => onOpen({ kind, item: c })} />
      ))}
    </div>
  );
}

export function CasesTab({ asset }: { asset: AssetOut }) {
  const cases = useCases(asset.id);
  const [open, setOpen] = useState<Open | null>(null);
  return (
    <div className="stack-md">
      <Toolbar onOpen={setOpen} />
      {cases.isPending && <LoadingCard label="Loading success cases" />}
      {cases.isError && <LoadFailure error={cases.error} onRetry={() => void cases.refetch()} />}
      {cases.isSuccess && <CaseList cases={cases.data.cases} onOpen={setOpen} />}
      <Dialogs open={open} asset={asset} cases={cases.data?.cases ?? []} setOpen={setOpen} />
    </div>
  );
}
